/**
 * 프로젝트 시드 — `project`, `project_detail`, `project_phase`(+`phase_log`), `project_member`.
 *
 * 프로젝트 본체는 뼈대만 담는다. 용도·규모·면적·공사비 같은 사무소 업무 지식은 상세 항목
 * (`project_detail`)이고, 항목 정의는 사무소가 더할 수 있다 — 주소와 사용승인은 기본 항목이
 * 아니라 이 사무소가 더한 것으로 둔다.
 *
 * 단계는 행이고 순서(`ordinal`)를 가진다. 날짜는 단계 행에 있다 — 시작일, 완료예정일, 완료일
 * 셋이다. PM 은 투입 인원의 `pm` 배치가 정본이고 한 프로젝트에 둘일 수 있다.
 */
import type { DetailField, DetailValueType, PhaseLog, ProjectStatus } from "@/types";

export interface MockProject {
  id: string;
  code: string | null;
  name: string;
  color: string;
  status: ProjectStatus;
  started_on: string | null;
  ended_on: string | null;
  memo: string;
  nas_path: string;
  /** 대표 이미지 — mock 은 올린 파일을 data URL 로 든다 */
  image_url?: string | null;
}

export interface MockPhase {
  id: string;
  project_id: string;
  name: string;
  template_key: string;
  ordinal: number;
  sort_order: number;
  start_label: string;
  end_label: string;
  start_date: string | null;
  due_date: string | null;
  done_date: string | null;
  logs: PhaseLog[];
}

export interface MockMember {
  id: string;
  project_id: string;
  user_id: string;
  assigned_on: string;
  released_on: string | null;
  /** 이 프로젝트의 PM 인가 — PM 은 사람이 아니라 배치에 붙는 이름표다 */
  is_pm: boolean;
}

export interface MockMemberPlan {
  id: string;
  project_member_id: string;
  period_from: string;
  period_to: string;
  /** 소수 둘째 자리까지 — 서버가 문자열로 준다 */
  daily_hours: string;
  note: string;
}

export interface MockDetail {
  project_id: string;
  field_id: string;
  value_text: string | null;
  value_number: number | null;
  value_date: string | null;
}

/* ── 상세 항목 정의 ────────────────────────────────────────────────────── */

const field = (
  key: string,
  label: string,
  value_type: DetailValueType,
  unit = "",
  options: { filterable?: boolean; seed?: boolean } = {}
): DetailField => ({
  id: `field-${key}`,
  key,
  label,
  value_type,
  unit,
  sort_order: 0,
  is_filterable: options.filterable ?? false,
  is_seed: options.seed ?? true,
});

/** 상세 항목 — 설치할 때 깔리는 열 항목과 주소. */
export const detailFields: DetailField[] = [
  field("usage", "용도", "text", "", { filterable: true }),
  field("scale", "규모", "text"),
  field("work_scope", "업무범위", "text", "", { filterable: true }),
  field("building_area", "건축면적", "area", "m²"),
  field("total_floor_area", "연면적", "area", "m²"),
  field("construction_area", "공사면적", "area", "m²"),
  field("construction_cost", "공사비", "money", "원"),
  field("constructor", "시공사", "text"),
  field("construction_started_on", "착공신고", "date"),
  field("address", "주소", "text"),
  field("use_approved_on", "사용승인", "date"),
].map((row, index) => ({ ...row, sort_order: (index + 1) * 10 }));

const FIELD_TYPE = new Map(detailFields.map((row) => [row.key, row.value_type]));

/* ── 프로젝트 ──────────────────────────────────────────────────────────── */

interface ProjectSeed extends Omit<
  MockProject,
  "started_on" | "ended_on" | "memo" | "nas_path"
> {
  started_on?: string;
  ended_on?: string;
  memo?: string;
  /** 상세 항목 값 — 항목 정의의 key 로 적는다 */
  details: Record<string, string | number>;
  /** 계약에 적는 발주처 이름 */
  client: string;
  pm: string;
}

/**
 * 사무소가 쓰던 LIFETHINGS 엑셀(backend `scripts/lifethings_20261102`)의 프로젝트 개요에서 이름 · 번호 ·
 * 용도 · 규모 · 면적 · 시공사를 가져왔다. 날짜와 금액은 시드 창(9월~10월)에 맞춰 새로 둔 것이다.
 * 발주처와 주소는 엑셀에 없어 이름에서 짐작되는 정도로만 적는다.
 *
 * 코드는 `YY-NNN`(엑셀 번호 2508 → 25-008). 진행 8개, 착수예정 2개, 완료 2개.
 */
const SEED: ProjectSeed[] = [
  {
    id: "p-2508",
    code: "25-008",
    name: "뉴 아모레 성수(재설계)",
    color: "#3182f6",
    status: "active",
    started_on: "2025-11-03",
    memo: "인접 대지 매입으로 재설계",
    client: "아모레퍼시픽",
    pm: "u-pm",
    details: {
      usage: "제1종근린생활시설(소매점), 제2종근린생활시설(일반음식점)",
      work_scope: "설계, 감리",
      scale: "지하 1층, 지상 5층",
      building_area: 287.88,
      total_floor_area: 1462.66,
      construction_area: 1464.5,
      construction_cost: 9_000_000_000,
      constructor: "씨앤오",
      address: "서울 성동구 성수동2가",
    },
  },
  {
    id: "p-2410",
    code: "24-010",
    name: "세종로공원 및 상징조형물",
    color: "#a234c7",
    status: "active",
    started_on: "2025-12-01",
    client: "서울특별시",
    pm: "u-pm2",
    details: {
      usage: "세종로공원(주차장, 근린생활시설)",
      work_scope: "설계",
      scale: "지상 1층(3개 동), 지하 6층",
      building_area: 1700,
      total_floor_area: 45324,
      address: "서울 종로구 세종로",
    },
  },
  {
    id: "p-2601",
    code: "26-001",
    name: "나진상가 공공기여",
    color: "#03b26c",
    status: "active",
    started_on: "2026-01-12",
    client: "나진상가 시행사",
    pm: "u-pm2",
    details: {
      usage: "공공기여시설",
      work_scope: "설계",
      address: "서울 용산구 한강로2가",
    },
  },
  {
    id: "p-2506",
    code: "25-006",
    name: "제주 한남숲길 산책로",
    color: "#18a5a5",
    status: "active",
    started_on: "2026-03-02",
    client: "오설록",
    pm: "u-pm",
    details: {
      usage: "공작물",
      work_scope: "설계, 제작",
      scale: "벤치, 사이니지",
      address: "제주 서귀포시 남원읍 한남리",
    },
  },
  {
    id: "p-2604",
    code: "26-004",
    name: "신정동 래미안 견본주택",
    color: "#fe9800",
    status: "active",
    started_on: "2026-04-06",
    client: "삼성물산 건설부문",
    pm: "u-pm2",
    details: {
      usage: "견본주택",
      work_scope: "설계",
      address: "서울 양천구 신정동",
    },
  },
  {
    id: "p-2511",
    code: "25-011",
    name: "북촌 대수선",
    color: "#f04452",
    status: "active",
    started_on: "2026-05-11",
    client: "윤서희 건축주",
    pm: "u-pm3",
    details: {
      usage: "공연장, 근린생활시설(소매점, 휴게음식점)",
      work_scope: "설계",
      scale: "에이동 지하 1층, 지상 4층 · 비동, 씨동 지상 1층",
      building_area: 310.93,
      total_floor_area: 1183.5,
      address: "서울 종로구 계동",
    },
  },
  {
    id: "p-2404",
    code: "24-004",
    name: "제주 한남다원 오설록 티하우스",
    color: "#03b26c",
    status: "active",
    started_on: "2026-06-01",
    client: "오설록",
    pm: "u-pm3",
    details: {
      usage: "근린생활시설(일반음식점, 사무소)",
      work_scope: "설계, 감리",
      scale: "주건축물 지상 1층, 부속건축물 2개 동",
      building_area: 713.25,
      total_floor_area: 695.88,
      construction_cost: 2_900_000_000,
      constructor: "코워커스",
      construction_started_on: "2026-09-25",
      address: "제주 서귀포시 남원읍 한남리",
    },
  },
  {
    id: "p-2503",
    code: "25-003",
    name: "삼표 트레이닝 센터 대수선 및 증축",
    color: "#a234c7",
    status: "active",
    started_on: "2026-06-22",
    client: "삼표",
    pm: "u-pm3",
    details: {
      usage: "교육연구시설",
      work_scope: "설계, 감리",
      scale: "지하 1층, 지상 2층",
      building_area: 1242.87,
      total_floor_area: 2613.07,
    },
  },
  {
    id: "p-2603",
    code: "26-003",
    name: "신흥 MST 공장",
    color: "#fe9800",
    status: "planned",
    memo: "계약 전 — 11월 초 계약 예정",
    client: "신흥",
    pm: "u-pm",
    details: {
      usage: "공장(의료기기 제조공장)",
      work_scope: "설계",
      address: "강원 원주시",
    },
  },
  {
    id: "p-contest",
    code: null,
    name: "광장동 복합체육시설 건립 사업 설계 공모",
    color: "#18a5a5",
    status: "planned",
    client: "광진구청",
    pm: "u-pm2",
    details: { usage: "체육시설", work_scope: "설계 공모", address: "서울 광진구 광장동" },
  },
  {
    id: "p-2305",
    code: "23-005",
    name: "명동 리모델링",
    color: "#3182f6",
    status: "done",
    started_on: "2023-05-22",
    ended_on: "2025-08-29",
    client: "(주)명동개발",
    pm: "u-pm2",
    details: {
      usage: "근린생활시설, 업무시설",
      work_scope: "설계, 감리",
      scale: "지하 2층, 지상 8층",
      building_area: 292.15,
      total_floor_area: 2573.98,
      construction_cost: 6_560_000_000,
      constructor: "청하종합건설(주)",
      construction_started_on: "2024-07-31",
      use_approved_on: "2025-07-28",
      address: "서울 중구 명동",
    },
  },
  {
    id: "p-2106",
    code: "21-006",
    name: "이태원 주택",
    color: "#f04452",
    status: "done",
    started_on: "2025-02-17",
    ended_on: "2026-08-28",
    client: "이태원 건축주",
    pm: "u-pm",
    details: {
      usage: "제2종근린생활시설(사무소), 단독주택",
      work_scope: "설계, 감리",
      scale: "지하 2층, 지상 3층, 2개 동",
      building_area: 308.95,
      total_floor_area: 1936.87,
      construction_cost: 5_880_000_000,
      constructor: "스튜가하우스",
      construction_started_on: "2026-02-27",
      use_approved_on: "2026-08-26",
      address: "서울 용산구 이태원동",
    },
  },
];

export const projects: MockProject[] = SEED.map((seed) => ({
  id: seed.id,
  code: seed.code,
  name: seed.name,
  color: seed.color,
  status: seed.status,
  started_on: seed.started_on ?? null,
  ended_on: seed.ended_on ?? null,
  memo: seed.memo ?? "",
  nas_path: seed.code ? `/projects/${seed.code}` : "",
}));

/** 발주처 이름 — 계약에 적는다. 거래처 테이블은 없다. */
export const CLIENT_NAMES: Record<string, string> = Object.fromEntries(
  SEED.map((seed) => [seed.id, seed.client])
);

export const projectDetails: MockDetail[] = SEED.flatMap((seed) =>
  Object.entries(seed.details).map(([key, value]) => {
    const type = FIELD_TYPE.get(key);
    return {
      project_id: seed.id,
      field_id: `field-${key}`,
      value_text: type === "text" ? String(value) : null,
      value_number: type === "number" || type === "area" || type === "money" ? Number(value) : null,
      value_date: type === "date" ? String(value) : null,
    };
  })
);

/* ── 단계 ──────────────────────────────────────────────────────────────── */

/** 설치할 때 깔리는 기본 단계. 이름표와 날짜 칸 이름은 backend `PHASE_TEMPLATE_SEED` 와 같다. */
const TEMPLATE = [
  { key: "pre_design", name: "기획설계", start_label: "계약일", end_label: "완료일" },
  { key: "basic_design", name: "기본설계", start_label: "시작일", end_label: "완료일" },
  { key: "permit", name: "허가접수", start_label: "접수일", end_label: "완료일" },
  { key: "working_design", name: "실시설계", start_label: "종료예정일", end_label: "종료일" },
  { key: "design_change", name: "설계변경", start_label: "종료예정일", end_label: "종료일" },
  { key: "supervision", name: "감리", start_label: "시작일", end_label: "사용승인" },
] as const;

type TemplateName = (typeof TEMPLATE)[number]["name"];

interface PhaseSetup {
  skip?: TemplateName[];
  /** 단계별 용역비(원) — 계약의 phase_fee 로 들어간다 */
  fee?: Partial<Record<string, number>>;
  insert?: { after: TemplateName; name: string; start_label: string; end_label: string }[];
}

const SETUP: Record<string, PhaseSetup> = {
  "p-2508": {
    fee: {
      기획설계: 20_000_000,
      기본설계: 90_000_000,
      허가접수: 40_000_000,
      실시설계: 150_000_000,
      설계변경: 20_000_000,
      감리: 110_000_000,
    },
  },
  "p-2410": {
    fee: {
      기획설계: 15_000_000,
      기본설계: 90_000_000,
      허가접수: 60_000_000,
      실시설계: 160_000_000,
    },
  },
  "p-2601": {
    skip: ["감리"],
    fee: { 기획설계: 8_000_000, 기본설계: 24_000_000, 허가접수: 10_000_000, 실시설계: 28_000_000 },
  },
  // 건축허가 대상이 아니라 산지 일시사용신고로 간다 — 허가접수 대신 끼운 단계.
  "p-2506": {
    skip: ["허가접수", "설계변경", "감리"],
    fee: { 기본설계: 15_000_000, "산지 일시사용신고": 5_000_000, 실시설계: 15_000_000 },
    insert: [
      { after: "기본설계", name: "산지 일시사용신고", start_label: "접수일", end_label: "수리일" },
    ],
  },
  "p-2604": {
    skip: ["감리"],
    fee: { 기획설계: 20_000_000, 기본설계: 60_000_000, 허가접수: 20_000_000, 실시설계: 80_000_000 },
  },
  "p-2511": {
    skip: ["감리"],
    fee: { 기획설계: 10_000_000, 기본설계: 30_000_000, 허가접수: 15_000_000, 실시설계: 45_000_000 },
  },
  "p-2404": {
    fee: {
      기본설계: 30_000_000,
      허가접수: 15_000_000,
      실시설계: 95_000_000,
      설계변경: 21_000_000,
      감리: 40_000_000,
    },
  },
  // 미디어스페이스와 홍보관 계획이 바뀌어 설계변경을 두 번 한다 — 2차를 뒤에 끼운다.
  "p-2503": {
    fee: {
      기획설계: 30_000_000,
      기본설계: 100_000_000,
      허가접수: 50_000_000,
      실시설계: 175_000_000,
      설계변경: 50_000_000,
    },
    insert: [
      { after: "설계변경", name: "설계변경 2차", start_label: "시작일", end_label: "종료일" },
    ],
  },
  "p-2603": {},
  "p-contest": {
    skip: ["허가접수", "실시설계", "설계변경", "감리"],
    insert: [{ after: "기본설계", name: "작품 제출", start_label: "접수일", end_label: "발표일" }],
  },
  "p-2305": {
    fee: {
      기획설계: 30_000_000,
      기본설계: 100_000_000,
      허가접수: 30_000_000,
      실시설계: 120_000_000,
      설계변경: 6_000_000,
      감리: 110_000_000,
    },
  },
  "p-2106": {
    fee: { 기본설계: 50_000_000, 허가접수: 20_000_000, 실시설계: 70_000_000, 감리: 60_000_000 },
  },
};

/** 단계 id — 프로젝트와 단계 이름으로 만든다(시드에서 찾기 쉽게). */
export const phaseId = (projectId: string, name: string) => `ph-${projectId.slice(2)}-${name}`;

/**
 * 단계 날짜 — 시작일, 완료예정일, 완료일.
 *  - 나진상가 공공기여 기본설계: 10/16 예정인데 아직 진행 중
 *  - 세종로공원 및 상징조형물 기본설계: 8/14 예정, 8/21 완료
 */
const DATES: {
  project: string;
  name: string;
  start: string | null;
  due: string;
  done: string | null;
}[] = [
  {
    project: "p-2508",
    name: "기획설계",
    start: "2025-11-03",
    due: "2025-12-19",
    done: "2025-12-19",
  },
  {
    project: "p-2508",
    name: "기본설계",
    start: "2026-06-01",
    due: "2026-09-11",
    done: "2026-09-11",
  },
  {
    project: "p-2508",
    name: "허가접수",
    start: "2026-08-17",
    due: "2026-09-04",
    done: "2026-09-04",
  },
  { project: "p-2508", name: "실시설계", start: "2026-09-14", due: "2026-10-30", done: null },
  // 겹치는 단계 — 실시설계를 마무리하는 중에 설계변경이 걸리고, 그 사이 착공해 감리가 붙었다.
  // 셋이 한 달 남짓 같이 간다. 공정표에서 단계가 층으로 쌓이는 경우다.
  { project: "p-2508", name: "설계변경", start: "2026-10-05", due: "2026-11-27", done: null },
  { project: "p-2508", name: "감리", start: "2026-10-19", due: "2027-01-29", done: null },
  {
    project: "p-2410",
    name: "기본설계",
    start: "2026-03-02",
    due: "2026-08-14",
    done: "2026-08-21",
  },
  { project: "p-2410", name: "허가접수", start: "2026-08-24", due: "2026-10-30", done: null },
  { project: "p-2410", name: "실시설계", start: null, due: "2027-02-26", done: null },
  {
    project: "p-2601",
    name: "기획설계",
    start: "2026-07-06",
    due: "2026-09-18",
    done: "2026-09-18",
  },
  { project: "p-2601", name: "기본설계", start: "2026-09-21", due: "2026-10-16", done: null },
  {
    project: "p-2506",
    name: "기본설계",
    start: "2026-03-02",
    due: "2026-08-28",
    done: "2026-08-28",
  },
  {
    project: "p-2506",
    name: "산지 일시사용신고",
    start: "2026-09-07",
    due: "2026-11-05",
    done: null,
  },
  // 신고 수리를 기다리면서 실시 도면을 넘겼다 — 둘이 3주쯤 겹친다.
  { project: "p-2506", name: "실시설계", start: "2026-10-12", due: "2026-12-18", done: null },
  {
    project: "p-2604",
    name: "기획설계",
    start: "2026-04-06",
    due: "2026-08-28",
    done: "2026-08-28",
  },
  { project: "p-2604", name: "기본설계", start: "2026-09-01", due: "2026-10-30", done: null },
  {
    project: "p-2511",
    name: "허가접수",
    start: "2026-06-15",
    due: "2026-08-14",
    done: "2026-08-13",
  },
  { project: "p-2511", name: "실시설계", start: "2026-08-17", due: "2026-10-23", done: null },
  {
    project: "p-2404",
    name: "실시설계",
    start: "2026-07-20",
    due: "2026-09-25",
    done: "2026-09-25",
  },
  { project: "p-2404", name: "감리", start: "2026-09-28", due: "2026-12-18", done: null },
  {
    project: "p-2503",
    name: "기본설계",
    start: "2026-07-06",
    due: "2026-10-02",
    done: "2026-10-02",
  },
  { project: "p-2503", name: "설계변경", start: "2026-10-05", due: "2026-11-06", done: null },
  { project: "p-2503", name: "설계변경 2차", start: null, due: "2026-12-11", done: null },
  { project: "p-2106", name: "감리", start: "2026-03-02", due: "2026-08-28", done: "2026-08-26" },
  { project: "p-2603", name: "기획설계", start: null, due: "2026-12-11", done: null },
  { project: "p-contest", name: "기본설계", start: "2026-10-19", due: "2026-11-13", done: null },
  { project: "p-contest", name: "작품 제출", start: null, due: "2026-12-04", done: null },
];

/**
 * 단계 진행 기록 — 마일스톤을 대신한다. 날짜와 한 줄, 그리고 예정인지 완료인지.
 * 예정으로 남은 기록이 「다가오는 일정」이고, 날짜가 지나면 밀린 것이다.
 */
const LOGS: {
  project: string;
  phase: string;
  date: string;
  status: "planned" | "done";
  text: string;
  author: string;
}[] = [
  {
    project: "p-2508",
    phase: "기획설계",
    date: "2026-09-04",
    status: "done",
    text: "인접 대지 매입 검토",
    author: "u-pm",
  },
  {
    project: "p-2508",
    phase: "실시설계",
    date: "2026-10-08",
    status: "done",
    text: "기전 킥오프 미팅",
    author: "u-pm",
  },
  {
    project: "p-2508",
    phase: "실시설계",
    date: "2026-10-30",
    status: "planned",
    text: "실시설계 1차 납품",
    author: "u-pm",
  },
  {
    project: "p-2410",
    phase: "허가접수",
    date: "2026-09-22",
    status: "done",
    text: "주차장 시스템 운영방식 비교 보고",
    author: "u-pm2",
  },
  {
    project: "p-2410",
    phase: "허가접수",
    date: "2026-10-23",
    status: "planned",
    text: "기본설계 공사비 보고",
    author: "u-pm2",
  },
  {
    project: "p-2410",
    phase: "허가접수",
    date: "2026-10-28",
    status: "planned",
    text: "건축허가 접수",
    author: "u-pm2",
  },
  {
    project: "p-2601",
    phase: "기본설계",
    date: "2026-10-16",
    status: "planned",
    text: "공공디자인 심의 자료 제출",
    author: "u-pm2",
  },
  {
    project: "p-2506",
    phase: "산지 일시사용신고",
    date: "2026-10-14",
    status: "done",
    text: "벤치·사이니지 위치도 전달",
    author: "u-pm",
  },
  {
    project: "p-2506",
    phase: "산지 일시사용신고",
    date: "2026-11-05",
    status: "planned",
    text: "산지 일시사용신고 수리",
    author: "u-pm",
  },
  {
    project: "p-2604",
    phase: "기본설계",
    date: "2026-10-14",
    status: "done",
    text: "발주처 미팅 6차",
    author: "u-pm2",
  },
  {
    project: "p-2604",
    phase: "기본설계",
    date: "2026-10-23",
    status: "planned",
    text: "발주처 미팅(VE 이슈)",
    author: "u-pm2",
  },
  {
    project: "p-2511",
    phase: "실시설계",
    date: "2026-10-23",
    status: "planned",
    text: "실시설계 최종도서 제출",
    author: "u-pm3",
  },
  {
    project: "p-2404",
    phase: "감리",
    date: "2026-09-28",
    status: "done",
    text: "공사 착공",
    author: "u-pm3",
  },
  {
    project: "p-2404",
    phase: "감리",
    date: "2026-10-21",
    status: "done",
    text: "시공 킥오프",
    author: "u-pm3",
  },
  {
    project: "p-2404",
    phase: "감리",
    date: "2026-10-28",
    status: "planned",
    text: "기초 배근 검측",
    author: "u-pm3",
  },
  {
    project: "p-2503",
    phase: "설계변경",
    date: "2026-10-13",
    status: "done",
    text: "실무진 보고(7차)",
    author: "u-pm3",
  },
  {
    project: "p-2503",
    phase: "설계변경",
    date: "2026-11-06",
    status: "planned",
    text: "경영진 보고(고문님)",
    author: "u-pm3",
  },
  {
    project: "p-contest",
    phase: "기획설계",
    date: "2026-10-22",
    status: "planned",
    text: "현장 설명회",
    author: "u-pm2",
  },
  {
    project: "p-contest",
    phase: "기획설계",
    date: "2026-10-29",
    status: "planned",
    text: "질의 회신 마감",
    author: "u-pm2",
  },
];

export const projectPhases: MockPhase[] = SEED.flatMap((seed) => {
  const setup = SETUP[seed.id] ?? {};
  const list: { key: string; name: string; start_label: string; end_label: string }[] =
    TEMPLATE.filter((row) => !setup.skip?.includes(row.name)).map((row) => ({
      key: row.key as string,
      name: row.name as string,
      start_label: row.start_label as string,
      end_label: row.end_label as string,
    }));
  for (const insert of setup.insert ?? []) {
    const at = list.findIndex((row) => row.name === insert.after);
    list.splice(at + 1, 0, {
      key: "",
      name: insert.name,
      start_label: insert.start_label,
      end_label: insert.end_label,
    });
  }
  return list.map((row, index) => {
    const dates = DATES.find((item) => item.project === seed.id && item.name === row.name);
    return {
      id: phaseId(seed.id, row.name),
      project_id: seed.id,
      name: row.name,
      template_key: row.key,
      ordinal: index + 1,
      sort_order: (index + 1) * 100,
      start_label: row.start_label,
      end_label: row.end_label,
      start_date: dates?.start ?? null,
      due_date: dates?.due ?? null,
      done_date: dates?.done ?? null,
      logs: LOGS.filter((log) => log.project === seed.id && log.phase === row.name).map(
        (log, position) => ({
          id: `log-${seed.id}-${row.name}-${position}`,
          log_date: log.date,
          status: log.status,
          text: log.text,
          author_id: log.author,
          created_at: `${log.date}T10:00:00+09:00`,
        })
      ),
    };
  });
});

/** 그날 진행 중이던 단계의 이름 — 시작했고 아직 끝나지 않은 단계. */
export function phaseNameOn(projectId: string, date: string): string | null {
  const phase = projectPhases.find(
    (row) =>
      row.project_id === projectId &&
      row.start_date !== null &&
      row.start_date <= date &&
      (row.done_date === null || date <= row.done_date)
  );
  return phase?.name ?? null;
}

/* ── 투입 인원 ─────────────────────────────────────────────────────────── */

/**
 * 누가 어느 프로젝트에 언제부터 배치되었나. `to` 가 있으면 그날 해제됐다 — 퇴사, 휴직한 사람.
 * Daily 시드도 이 표를 읽는다 — 한곳에서 정해야 어긋나지 않는다.
 */
export const TEAMS: { project_id: string; user_id: string; from: string; to?: string }[] = [
  { project_id: "p-2508", user_id: "u-pm", from: "2025-11-03" },
  { project_id: "p-2508", user_id: "u-staff", from: "2025-11-03" },
  { project_id: "p-2508", user_id: "u-st4", from: "2026-02-02" },
  { project_id: "p-2508", user_id: "u-pm3", from: "2026-03-16", to: "2026-10-09" },
  { project_id: "p-2508", user_id: "변경민", from: "2025-11-03" },
  { project_id: "p-2410", user_id: "u-pm2", from: "2025-12-01" },
  { project_id: "p-2410", user_id: "u-st2", from: "2025-12-01" },
  { project_id: "p-2601", user_id: "u-pm2", from: "2026-01-12" },
  { project_id: "p-2601", user_id: "u-st2", from: "2026-01-12" },
  { project_id: "p-2601", user_id: "u-st6", from: "2026-01-12", to: "2026-09-30" },
  { project_id: "p-2506", user_id: "u-pm", from: "2026-03-02" },
  { project_id: "p-2506", user_id: "u-staff", from: "2026-03-02" },
  { project_id: "p-2506", user_id: "u-st5", from: "2026-07-06" },
  { project_id: "p-2604", user_id: "u-pm2", from: "2026-04-06" },
  { project_id: "p-2604", user_id: "u-st2", from: "2026-04-06" },
  { project_id: "p-2604", user_id: "u-st5", from: "2026-07-06" },
  { project_id: "p-2511", user_id: "u-pm3", from: "2026-05-11", to: "2026-10-09" },
  { project_id: "p-2511", user_id: "u-pm", from: "2026-05-11" },
  { project_id: "p-2511", user_id: "u-st3", from: "2026-05-11" },
  { project_id: "p-2404", user_id: "u-pm3", from: "2026-06-01", to: "2026-10-09" },
  { project_id: "p-2404", user_id: "u-st3", from: "2026-06-01" },
  { project_id: "p-2404", user_id: "u-st5", from: "2026-07-06" },
  { project_id: "p-2503", user_id: "u-pm3", from: "2026-06-22", to: "2026-10-09" },
  { project_id: "p-2503", user_id: "u-st4", from: "2026-06-22" },
  { project_id: "p-2603", user_id: "u-pm", from: "2026-11-02" },
  { project_id: "p-contest", user_id: "u-pm2", from: "2026-10-19" },
  { project_id: "p-contest", user_id: "u-st3", from: "2026-10-19" },
  { project_id: "p-2305", user_id: "u-pm2", from: "2023-05-22", to: "2025-08-29" },
  { project_id: "p-2106", user_id: "u-pm", from: "2025-02-17", to: "2026-08-28" },
];

/** 시드에서 그 프로젝트를 이끄는 사람 — 그 사람의 배치에 PM 표시를 단다. */
export const PM_OF = new Map(SEED.map((seed) => [seed.id, seed.pm]));

export const projectMembers: MockMember[] = TEAMS.map((team, index) => ({
  id: `member-${index + 1}`,
  project_id: team.project_id,
  user_id: team.user_id,
  assigned_on: team.from,
  released_on: team.to ?? null,
  is_pm: PM_OF.get(team.project_id) === team.user_id,
}));

/** 배치가 끝나지 않은 사람의 계획은 여기까지 그린다 — 끝없는 막대를 그릴 수는 없다. */
const PLAN_HORIZON = "2027-06-30";

/**
 * 투입 계획 — Process 화면의 배치 캡슐 하나가 이 한 행이다.
 *
 * 배치 기간을 그대로 한 칸으로 옮긴다. 사람마다 들어오는 날이 다르고 몇은 중간에 빠지므로,
 * 화면이 그릴 합류·이탈 모양이 이 데이터에 그대로 들어 있다.
 */
export const projectMemberPlans: MockMemberPlan[] = projectMembers.map((member, index) => {
  const project = projects.find((row) => row.id === member.project_id);
  const closes = member.released_on ?? project?.ended_on ?? PLAN_HORIZON;
  return {
    id: `plan-${index + 1}`,
    project_member_id: member.id,
    period_from: member.assigned_on,
    period_to: closes < member.assigned_on ? member.assigned_on : closes,
    daily_hours: PM_OF.get(member.project_id) === member.user_id ? "4.00" : "2.00",
    note: PM_OF.get(member.project_id) === member.user_id ? "총괄" : "",
  };
});

/** 그 프로젝트의 단계별 용역비 — 계약 시드가 읽는다. */
export const phaseFeesOf = (projectId: string) =>
  Object.entries(SETUP[projectId]?.fee ?? {}).map(([name, fee]) => ({
    phase_id: phaseId(projectId, name),
    fee: fee ?? 0,
    memo: "",
  }));

/** 그날 기준 7일 안에 다가오는 예정 기록 — Daily 시드의 한 일 문구를 고르는 데 쓴다. */
export function plannedLogNear(projectId: string, date: string): string | null {
  const limit = new Date(Date.parse(`${date}T00:00:00Z`) + 7 * 86_400_000)
    .toISOString()
    .slice(0, 10);
  const phase = projectPhases.find(
    (row) =>
      row.project_id === projectId &&
      row.logs.some(
        (log) => log.status === "planned" && log.log_date >= date && log.log_date <= limit
      )
  );
  return (
    phase?.logs.find(
      (log) => log.status === "planned" && log.log_date >= date && log.log_date <= limit
    )?.text ?? null
  );
}
